import getConfig from 'next/config';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { setupCache, buildMemoryStorage, buildKeyGenerator } from 'axios-cache-interceptor';
import hash from 'object-hash';

export type Variables = { [key: string]: any };
export type RequestOptions = AxiosRequestConfig;

export interface GraphQLRequest<V> {
  query: string,
  variables?: V,
}

export interface GraphQLClient {
  query<T = any, V = Variables>(request: GraphQLRequest<V>): Promise<{ data: T }>,
}

let graphQlClient: GraphQLClient = null;

class GraphQLClientImpl implements GraphQLClient {
  private url: string;

  private options: RequestOptions;

  private instance: AxiosInstance;

  constructor(url: string, options?: RequestOptions) {
    this.url = url;
    this.options = options || {};

    this.instance = setupCache(axios, {
      generateKey: buildKeyGenerator((request) => ({
        url: request.url,
        method: request.method,
        custom: hash({
          url: request.url,
          method: request.method,
          data: request.data,
          headers: request.headers,
        }),
      })),
      methods: ['post'],
      ttl: 1000 * 60 * 15, // 15 minutes
      interpretHeader: false, // disable cache-control
      storage: buildMemoryStorage(),
    });
  }

  async query<T = any, V = Variables>(request: GraphQLRequest<V>): Promise<{ data: T }> {
    // unwrap from axios 'data' and use only graphql 'data'
    return this.instance.post<{ data: T }>(this.url, request, this.options)
      .then((response: AxiosResponse<{ data: T }>) => response.data);
  }
}

function create() {
  const {
    publicRuntimeConfig: {
      GRAPHCMS_URL,
      GRAPHCMS_AUTH_TOKEN,
    },
  } = getConfig();

  return new GraphQLClientImpl(GRAPHCMS_URL, {
    headers: {
      Authorization: GRAPHCMS_AUTH_TOKEN ? `Bearer ${GRAPHCMS_AUTH_TOKEN}` : '',
    },
  });
}

export default function initGraphQLClient() {
  if (!graphQlClient) {
    graphQlClient = create();
  }

  return graphQlClient;
}
